"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var React = require("react");
/**
 * Generator that applies a validator function recursively to all child elements.
 * @param validatorFn Function that accepts a `React.ReactChild` node and returns a boolean if the child is invalid.
 * @param errorFn Function that is used to generate the error string in case an error occurs.
 * @returns Function that can be applied to the `children` property within a component's propTypes definition.
 */
function childTreeValidator(validatorFn, errorFn) {
    return function (props, propName, componentName) {
        var result = null;
        var prop = props[propName];
        function isInvalidChild(child) {
            var hasOwnProperty = Object.prototype.hasOwnProperty;
            var isInvalid = !validatorFn(child); // ensure that it is a valid child
            if (hasOwnProperty.call(child, 'props')) {
                // it's safe to cast to ReactElement in case a `props` property is available
                var element = child;
                // In case children are available, those have to be tested recursively.
                if (hasOwnProperty.call(element.props, 'children')) {
                    return isInvalid || containsInvalidChild(element.props.children);
                }
            }
            return isInvalid;
        }
        function containsInvalidChild(children) {
            return React.Children.toArray(children).some(isInvalidChild);
        }
        if (propName === 'children' && prop && containsInvalidChild(prop)) {
            result = new Error(errorFn(componentName));
        }
        return result;
    };
}
exports.childTreeValidator = childTreeValidator;
function autoFormatDate(value) {
    return value
        .replace(/\.{2,}/g, ".") // replace any multi-dots by a single dot
        .replace(/^\.$/g, "") // if only a dot has been entered, remove it
        .replace(/^(\d)\.$/g, "0$1.") // add leading zero for day if necessary
        .replace(/^(\d\d)$/g, "$1.") // add dot after day
        .replace(/^(\d\d)(\d)$/g, "$1.$2") // add dot between day and month (only possible when deleting text)
        .replace(/^(\d\d)\.(\d)\.$/g, '$1.0$2.') // add leading zero to month if necessary
        .replace(/^(\d\d\.\d\d)$/g, '$1.') // add dot after month
        .replace(/^(\d\d\.\d\d)(\d)$/g, '$1.$2') // add dot between month and year (only possible when deleting text)
        .replace(/[^\d\.]/g, ''); // remove any char that is neither a dot nor a number
}
exports.autoFormatDate = autoFormatDate;
/**
 * Takes the value of a children property and returns an array of all of these
 * children that are elements. It flattens the children, that means, if a child
 * is an array of elements, these elements are considered as children.
 *
 * It especially omits booleans, null, undefined and text.
 */
function getChildElements(children) {
    var childrenAsArray = React.Children.toArray(children);
    return childrenAsArray.filter(function (child) { return React.isValidElement(child); });
}
exports.getChildElements = getChildElements;
/* Takes the value of a children property and returns an array of all these
 * children that are elements of a certain type. It flattens the children,
 * that means, if a child is an array of elements, these elements are considered
 * as children.
 *
 * It especially omits booleans, null, undefined and text
 */
function getChildElementsOfType(children, type) {
    return getChildElements(children).filter(function (child) { return child.type === type; });
}
exports.getChildElementsOfType = getChildElementsOfType;
function isElementContainedWithin(childElement, hostElement) {
    if (!(childElement instanceof Element) || !(hostElement instanceof Element)) {
        return false;
    }
    return hostElement.contains(childElement);
}
exports.isElementContainedWithin = isElementContainedWithin;
/**
 * This method does call the specified onBlur element only if the focus of the current selection
 * is outside of the specified host element. This method is therefore useful for components
 * that contain multiple control inputs.
 * <br />
 * It does stop the propagation of the focus event.
 *
 * @param {FocusEvent} e the event
 * @param {HTMLElement} hostElement the host element of the controls
 * @param {(value: T) => void} onBlur the blur function
 * @param {T} value the control value
 */
function handleBlurOutside(e, hostElement, onBlur, value) {
    var relatedTarget = e.relatedTarget;
    // React event bubbeling must be stopped in any case because we call onBlur on our own.
    e.stopPropagation();
    // IE11 compatibility: IE11 does not have related target on events, but activeElement is always
    // set on the document and refers to the target element.
    var targetElement = relatedTarget || document.activeElement;
    if (!isElementContainedWithin(targetElement, hostElement)) {
        onBlur(value);
    }
}
exports.handleBlurOutside = handleBlurOutside;
//# sourceMappingURL=Components.js.map
